// Web Push (background) support - registers a service worker and stores a
// PushSubscription in Supabase. Only runs in the published/deployed app, never
// inside the Lovable editor preview iframe.
import { supabase } from "@/integrations/supabase/client";

// Public VAPID key - safe to embed.
export const VAPID_PUBLIC_KEY =
  "BBTNCkU5aRHk0sMu_H27NkHwPos8qUHusvXlnLjEODKotM-n7SRiK8avNMa_0UYEQHSzJjuXBDj-ztwq9iK2Yes";

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  typeof window !== "undefined" &&
  (window.location.hostname.includes("id-preview--") ||
    window.location.hostname.includes("lovableproject.com") ||
    window.location.hostname.includes("lovable.dev"));

export const isBackgroundPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window &&
  !isInIframe &&
  !isPreviewHost;

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isBackgroundPushSupported()) {
    // Aggressively unregister any SW that may have been left behind in preview/iframe.
    if ("serviceWorker" in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch {}
    }
    return null;
  }
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.warn("SW registration failed", e);
    return null;
  }
};

const arrayBufferToBase64 = (buf: ArrayBuffer | null) => {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

export const subscribeAndStorePush = async (): Promise<boolean> => {
  if (!isBackgroundPushSupported()) return false;
  const reg = await registerServiceWorker();
  if (!reg) return false;
  if (Notification.permission !== "granted") return false;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    } catch (e) {
      console.warn("PushManager.subscribe failed", e);
      return false;
    }
  }

  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return false;

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  const endpoint = sub.endpoint;
  const p256dh = json.keys?.p256dh || arrayBufferToBase64(sub.getKey("p256dh"));
  const authKey = json.keys?.auth || arrayBufferToBase64(sub.getKey("auth"));
  if (!endpoint || !p256dh || !authKey) return false;

  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      user_id: auth.user.id,
      endpoint,
      p256dh,
      auth: authKey,
      user_agent: navigator.userAgent,
    },
    { onConflict: "endpoint" },
  );
  if (error) {
    console.warn("Failed to save push subscription", error);
    return false;
  }
  return true;
};

export const unsubscribePush = async () => {
  if (!isBackgroundPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await supabase.from("push_subscriptions").delete().eq("endpoint", endpoint);
    }
  } catch {}
};
